import moment from "moment";
import {
  capitalizeFirstLetter,
  flattenObj,
  getKeyValueInNestedObject,
  getPath,
  isValNull,
  removeSymbolfromString,
} from "../../../utils";

const aadhaar_format = /^[0-9]{12}$/;
const dl_format = /^[A-Za-z0-9]+$/;
const pan_format = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const ifsc_format = /^[A-Z]{4}[0-9][A-Z0-9]{6}$/;
const account_no_format = /^[0-9]{9,18}$/;
const phone_no_format = /^[0-9]{10}$/;
const today_date = moment().subtract(1, "days");
const pin_code_format = /^[0-9]{6}$/;

const errorMessages = [
  {
    keys: ["dob"],
    format: today_date,
    equator: "lt", //less than
    message: "DOB should be less than current date",
  },
  {
    keys: ["phone_no", "contact_no"],
    format: phone_no_format,
    equator: "eq", //equal to
    message: "Please enter a valid phone number (1234567890)",
  },
  {
    keys: ["aadhar_id"],
    format: aadhaar_format,
    equator: "eq",
    message: "Aadhar number should be all numbers with length of 12",
  },
  {
    keys: ["license_no"],
    format: dl_format,
    equator: "eq",
    message: `Please enter correct license number in format:- ${dl_format}`,
  },
  {
    keys: ["marriage_date"],
    format: today_date,
    equator: "lt",
    message: "Marriage should be less than current date",
  },
  {
    keys: ["account_no"],
    format: account_no_format,
    equator: "eq",
    message: `Please enter correct account number in format:- ${account_no_format}`,
  },
  {
    keys: ["ifsc_code"],
    format: ifsc_format,
    equator: "eq",
    message: `Please enter a valid IFSC code (ABCD1234567) or (XXXX1111111)`,
  },
  {
    keys: ["pan_no"],
    format: pan_format,
    equator: "eq",
    message: `Please enter a valid PAN number (ABCDE1234F) or (XXXXX1111X)`,
  },
  {
    keys: ["zone_id"],
    message: `Please enter zone_id`,
  },
  {
    keys: ["current_address_object.pin_code", "permanent_address_object.pin_code"],
    format: pin_code_format,
    equator: "eq",
    message: "Pincode/Zipcode should be of 6 digits",
  },
];

const basicDetailsMandatoryFields = [
  "name",
  "city_id",
  "locality_id",
  "phone_no",
  "current_address_object.address",
  "current_address_object.city",
  "current_address_object.state",
  "current_address_object.country",
  "current_address_object.pin_code",
  "permanent_address_object.address",
  "permanent_address_object.city",
  "permanent_address_object.state",
  "permanent_address_object.country",
  "permanent_address_object.pin_code",
];
const basicDetailsOnBoardingMandatoryFields = [
  "name",
  "city_id",
  "locality_id",
  "phone_no",
  "current_address_object.address",
  "current_address_object.city",
  "current_address_object.state",
  "current_address_object.country",
  "current_address_object.pin_code",
];
const additionalDetailsmandatoryFields = [
  "family_size",
  "native_place",
  "profession_id",
  "pursuing_education",
  "single_earner",
  "educational_qualification_id",
];
const financialDetailsMandatoryFields = [
  "bank_name",
  "account_no",
  "ifsc_code",
  "pan_no",
  "name",
  "relation",
  "dob",
  "contact_no",
];

export const nestedMandatoryKey = ["address.name", "address.phone_no"];

const getNestedKey = (key) => {
  const arr = key.split(".");
  return arr[arr.length - 1];
};

const isErrorKey = (key) => {
  let flag = 0;
  for (let i = 0; i < errorMessages.length; i++) {
    if (errorMessages[i].keys.includes(key)) {
      flag = errorMessages[i];
      break;
    }
  }
  return flag;
};

const getMandatoryFields = (type) => {
  if (type === "basicDetails") {
    return basicDetailsMandatoryFields;
  } else if (type === "basicDetailsOnboarding") {
    return basicDetailsOnBoardingMandatoryFields;
  } else if (type === "additionalDetails") {
    return additionalDetailsmandatoryFields;
  } else {
    return financialDetailsMandatoryFields;
  }
};

const checkForMandatoryFields = (payload, type) => {
  const err = [];
  const mandatoryFields = getMandatoryFields(type);
  const flattenedObj = flattenObj(payload);
  mandatoryFields.forEach((field) => {
    const keyVal = getKeyValueInNestedObject(payload, field);
    if (isValNull(keyVal)) {
      err.push(
        `Please enter ${capitalizeFirstLetter(
          removeSymbolfromString(field, "_")
        )}`
      );
    }
  });
  Object.keys(flattenedObj).map((key) => {
    const finalKey = getNestedKey(key);
    const keyVal = getPath(payload, key, "");
    if (mandatoryFields.includes(finalKey) && isValNull(keyVal)) {
      err.push(
        `Please enter ${capitalizeFirstLetter(
          removeSymbolfromString(
            `${key.includes("nominee") ? "nominee " : ""}${finalKey}`,
            "_"
          )
        )}`
      );
    }
  });
  return Array.from(new Set(err));
};

export const handleValidations = (
  payload = {},
  type,
  basicDetails = {},
  financialDetails = {}
) => {
  let err = [];
  if (Object.keys(payload).length === 0) {
    return err;
  }
  const flattenedObj = flattenObj(payload);
  //check for mandatoryFields
  const mandatoryErrors = checkForMandatoryFields(payload, type);
  if (mandatoryErrors.length > 0) {
    err = [...err, ...mandatoryErrors];
  }
  Object.keys(flattenedObj).map((key) => {
    const finalKey = getNestedKey(key);
    const errorFormat = isErrorKey(finalKey);
    const keyVal = getPath(payload, key, "");
    if (errorFormat) {
      if (errorFormat.equator === "eq") {
        if (keyVal && !errorFormat.format.test(keyVal)) {
          err.push(errorFormat.message);
        }
      } else {
        if (keyVal && moment(keyVal) > errorFormat.format) {
          err.push(errorFormat.message);
        }
        if (
          finalKey === "zone_id" &&
          isValNull(keyVal) &&
          getPath(basicDetails, "zone_enabled", false)
        ) {
          err.push(errorFormat.message);
        }
        if (finalKey === "dob" && keyVal) {
          //check age should be more than 18
          const yearsPassed = errorFormat.format.diff(moment(keyVal), "years");
          if (yearsPassed < 18) {
            err.push("Age should be more than 18 years, enter correct dob");
          }
          if (yearsPassed > 120) {
            err.push("Age should be less than 120 years, enter correct dob");
          }
        }
        if (finalKey === "marriage_date" && basicDetails.dob && keyVal) {
          //person should be atleast 18 when married
          const yearsPassed = moment(basicDetails.dob).diff(
            moment(keyVal),
            "years"
          );
          if (Math.abs(yearsPassed) < 18) {
            err.push(
              "Delivery partner should be more than 17 years of age to get married"
            );
          }
        }
      }
    }
    if (finalKey === "contact_no") {
      //check nominee contact no with driver phone no, they shouldnt match
      if (keyVal === basicDetails.phone_no) {
        err.push(
          "nominee contact number cannot be same as driver phone number"
        );
      }
    } else if (finalKey === "phone_no") {
      //check nominee contact no with driver phone no, they shouldnt match
      if (
        keyVal === getPath(financialDetails, "nominee.contact_no", undefined)
      ) {
        err.push(
          "driver phone number cannot be same as nominee contact number"
        );
      }
      if (keyVal === basicDetails.emergency_contact_no) {
        err.push(
          "driver phone number cannot be same as emergency contact number"
        );
      }
    } else if (finalKey === "emergency_contact_no") {
      if (keyVal === basicDetails.phone_no) {
        err.push(
          "emergency contact number cannot be same as driver phone number"
        );
      }
    } else if (finalKey === "boy_child_no" || finalKey === "girl_child_no") {
      if (keyVal < 0 || keyVal > 11) {
        err.push("Number of childrens should be between between 0 and 11");
      }
    } else if (finalKey === "family_size") {
      if (keyVal < 1 || keyVal > 15) {
        err.push("Family size should be between between 1 and 15");
      }
    } else if (finalKey === "license_expiry_date") {
      //next acceptable date should be 1 month ahead
      const monthsPassed = moment().diff(moment(keyVal), "months");
      if (moment(keyVal) > moment() && Math.abs(monthsPassed) < 1) {
        err.push(
          "Your license expires within a month, please renew and try again"
        );
      }
    } else if (finalKey === "name") {
      if (keyVal.length < 3) {
        err.push(
          `${
            key.includes("nominee") ? "Nominee name" : "Name"
          } should be minimum 3 characters`
        );
      }
    }
  });
  return err;
};
